import { CashTransaction } from '@/@types/cash-management';
import { PrintLayout } from '@/@types/print';
import {
  POS_PRINT_BOLD_FONT_WEIGHT,
  POS_PRINT_FONT_FAMILY,
  POS_PRINT_80MM_FONT_WEIGHT,
  POS_PRINT_RENDERING_STYLES,
  Separator,
} from '@/components/common/PosPrint';
import { COMPANY_CNPJ, COMPANY_NAME } from '@/constants';
import { PRINT_LAYOUT_1, PRINT_LAYOUT_2 } from '@/modules/utils/print-layouts';
import { ReactNode, Ref } from 'react';
import { transactionTypeLabel } from './cash-management.utils';

type CashSessionManualMovementPosPrintProps = {
  transaction?: CashTransaction;
  containerRef: Ref<HTMLDivElement>;
  selectedLayout?: PrintLayout;
};

function CashSessionPosHeader() {
  return (
    <div
      style={{
        textAlign: 'center',
        fontWeight: POS_PRINT_BOLD_FONT_WEIGHT,
      }}
    >
      <div>{COMPANY_NAME}</div>
      <div>{COMPANY_CNPJ}</div>
    </div>
  );
}

function CenteredTitle({ children }: { children: string }) {
  return (
    <div
      style={{
        textAlign: 'center',
        fontWeight: POS_PRINT_BOLD_FONT_WEIGHT,
      }}
    >
      {children}
    </div>
  );
}

function Row({
  children,
  selectedLayout,
}: {
  children: ReactNode;
  selectedLayout: PrintLayout;
}) {
  return (
    <div
      style={{
        paddingLeft: selectedLayout === PRINT_LAYOUT_2 ? '0.25cm' : '0',
        whiteSpace: 'pre-wrap',
      }}
    >
      {children}
    </div>
  );
}

function TwoColumnRow({
  left,
  right,
  selectedLayout,
}: {
  left: string;
  right: string;
  selectedLayout: PrintLayout;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <span
        style={{
          paddingLeft: selectedLayout === PRINT_LAYOUT_2 ? '0.25cm' : '0',
        }}
      >
        {left}
      </span>
      <span>{right}</span>
    </div>
  );
}

function formatPosCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function CashSessionManualMovementPosPrint({
  transaction,
  containerRef,
  selectedLayout = PRINT_LAYOUT_1,
}: CashSessionManualMovementPosPrintProps) {
  if (!transaction?.cashSession) return <div ref={containerRef} />;

  return (
    <div
      ref={containerRef}
      style={{
        printColorAdjust: 'economy',
        width: '7.2cm',
        color: 'black',
        padding: '0.25cm',
        fontFamily: POS_PRINT_FONT_FAMILY,
        fontSize: '11px',
        fontWeight: POS_PRINT_80MM_FONT_WEIGHT,
        ...POS_PRINT_RENDERING_STYLES,
      }}
    >
      <CashSessionPosHeader />
      <Separator />
      <CenteredTitle>{transactionTypeLabel[transaction.type]}</CenteredTitle>
      <Separator />
      <TwoColumnRow
        left={`NUMERO: ${transaction.number}`}
        right={`CAIXA: ${transaction.cashSession.number}`}
        selectedLayout={selectedLayout}
      />
      <TwoColumnRow
        left={`DATA: ${transaction.createdAt}`}
        right={`VALOR: ${formatPosCurrency(transaction.amount)}`}
        selectedLayout={selectedLayout}
      />
      <Row selectedLayout={selectedLayout}>DESCRICAO:</Row>
      <Row selectedLayout={selectedLayout}>
        {transaction.description || '-'}
      </Row>
      <Row selectedLayout={selectedLayout}>
        FORMA PAGTO: {transaction.documentType?.name || '-'}
      </Row>
      <Separator />
      <br />
      <Row selectedLayout={selectedLayout}>Assinatura:</Row>
      <br />
      <br />
    </div>
  );
}
